import { isCustomRolesEnabled, isTeamsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
// eslint-disable-next-line no-restricted-imports
import type { OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, List, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import {
  isEmail,
  isLength,
  isNotEmpty,
  optional,
  validateCustomRoles,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks } from '#utils/access/types';
import type { OAuthProviderKind } from '#utils/accounts/accountTypes';
import {
  GITHUB_AUTH_KIND,
  GOOGLE_AUTH_KIND,
  MFA_DISABLED,
  MFA_RESET,
  PROFILE_NAME_MAX_LENGTH,
  VERCEL_MARKETPLACE_AUTH_KIND,
} from '#utils/accounts/accountTypes';
import { nilFilter } from '#utils/collectionUtils';
import type { Link } from '#utils/linkUtils';
import type { RoleSummaryType } from '#utils/roleUtils';

import { MemberPermissionGrants } from './MemberPermissionGrants';
import type { MemberSummary } from './MemberSummary';
import { MemberTeams } from './MemberTeams';

type lastSeenMetadata = ImmutableMap<{
  tokenId?: string;
}>;

const memberFullName = (m: MemberSummary | Member) => [m.firstName, m.lastName].join(' ').trim();
const memberDisplayName = (m: MemberSummary | Member): string => m.getFullName() || m.email;

export type NormalizedMembersCollectionResponse = ImmutableMap<{
  entities: ImmutableMap<{
    members: OrderedMap<string, Member>;
  }>;
  result: ImmutableMap<{
    items: string[];
    totalCount: number;
    totalCountWithoutSelfOrOwner?: number;
    _links: ImmutableMap<{
      last: {
        href: string;
        type: string;
      };
      next: {
        href: string;
        type: string;
      };
      self: {
        href: string;
        type: string;
      };
    }>;
  }>;
}>;

export type MemberType = {
  _access: null | AccessChecks;
  _links: Link;
  _id: string;
  _pendingInvite: boolean;
  // Set the default to true for legacy users who do not have the verification property defined
  _verified: boolean;
  _pendingEmail: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  customRoles: Set<string>;
  customRolesInfo: Set<RoleSummaryType>;
  permissionGrants: List<MemberPermissionGrants>;
  mfa: 'enabled' | 'disabled' | 'reset';
  excludedDashboards: Set<string>;
  teams?: List<MemberTeams>;
  _lastSeen: number;
  _lastSeenMetadata: null | lastSeenMetadata;
  _avatarUrl: string;
  creationDate: number;
  canReview?: boolean;
  oauthProviders: List<OAuthProviderKind>;
  version?: number;
  roleAttributes?: Map<string, string[]>;
  mfaEnforced?: boolean;
};

export class Member extends Record<MemberType>({
  _access: null,
  _links: Map(),
  _id: '',
  _pendingInvite: false,
  // Set the default to true for legacy users who do not have the verification property defined
  _verified: true,
  _pendingEmail: '',
  email: '',
  firstName: '',
  lastName: '',
  role: '',
  customRoles: Set(),
  customRolesInfo: Set(),
  permissionGrants: List(),
  mfa: MFA_DISABLED,
  excludedDashboards: Set(),
  teams: List(),
  _lastSeen: -1,
  _lastSeenMetadata: null,
  _avatarUrl: '',
  creationDate: 0,
  canReview: undefined,
  oauthProviders: List(),
  version: 0,
  roleAttributes: Map(),
  mfaEnforced: false,
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this.get('_access');

    if (this.isOwner()) {
      return () => createAccessDecision({ isAllowed: false });
    }

    if (profile._id === this._id) {
      return () =>
        createAccessDecision({ isAllowed: false, customMessage: 'You may not modify this setting for yourself' });
    }

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isWriter()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Writer' });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  validate(forceCustom: boolean) {
    return validateRecord(
      this,
      optional(isNotEmpty)('firstName'),
      isLength(0, PROFILE_NAME_MAX_LENGTH)('firstName'),
      optional(isNotEmpty)('lastName'),
      isLength(0, PROFILE_NAME_MAX_LENGTH)('lastName'),
      isEmail('email'),
      validateCustomRoles<Member>((member) => member.customRoles, forceCustom),
    );
  }

  selfLink() {
    return this._links.getIn(['canonical', 'href']) || this._links.getIn(['self', 'href']);
  }

  getFullName() {
    return memberFullName(this as Member);
  }

  getDisplayName() {
    return memberDisplayName(this as Member);
  }

  isOwner() {
    return !this.hasCustomRoles() && this.role === 'owner';
  }

  isAdmin() {
    return !this.hasCustomRoles() && this.role === 'admin';
  }

  isAdminOrOwner() {
    return this.isAdmin() || this.isOwner();
  }

  isWriter() {
    return (
      this.role === 'writer' && !this.hasCustomRoles() && !this.hasTeamCustomRoles() && !this.hasPermissionGrants()
    );
  }

  isReader() {
    return (
      this.role === 'reader' && !this.hasCustomRoles() && !this.hasTeamCustomRoles() && !this.hasPermissionGrants()
    );
  }

  hasAdminRights() {
    return this.isOwner() || this.isAdmin() || this.hasCustomRoles() || this.hasTeamCustomRoles();
  }

  hasWriterRights() {
    return this.hasAdminRights() || this.isWriter() || this.hasCustomRoles() || this.hasPermissionGrants();
  }

  // TODO: When custom role restriction is fully rolled out across all actions,
  // replace hasWriterRights with this.
  hasStrictWriterRights() {
    return this.isAdmin() || this.isWriter() || this.isOwner();
  }

  hasTeamCustomRoles() {
    let hasTeamRoles = false;
    if (this.teams) {
      this.teams.forEach((team) => {
        if (team.hasTeamCustomRoles()) {
          hasTeamRoles = true;
          return;
        }
      });
    }
    return hasTeamRoles;
  }

  getTeamsMaintained() {
    return this.permissionGrants.map((permissionGrant) => {
      const [type, value] = permissionGrant.parseResource();
      if (type === 'team' && permissionGrant.isTeamMaintainer()) {
        return value;
      }
      return undefined;
    });
  }

  getAllMemberTeamKeys() {
    const memberTeams = this.teams?.map((team) => team?.key);

    if (!memberTeams) {
      return Set<string>();
    }

    const teamsMaintainedByMember = this.getTeamsMaintained().filter(nilFilter);
    return memberTeams.concat(teamsMaintainedByMember).toSet();
  }

  hasPermissionGrants() {
    return !this.permissionGrants.isEmpty();
  }

  hasOnlyTeamCustomRoles() {
    return this.hasTeamCustomRoles() && !this.hasCustomRoles();
  }

  hasCustomRoles() {
    return isTeamsEnabled() && isCustomRolesEnabled() && !this.customRoles.isEmpty();
  }

  hasPendingInvite() {
    return this._pendingInvite;
  }

  isVerified() {
    return this._verified;
  }

  verificationEmail() {
    return this._pendingEmail;
  }

  hasMFA() {
    return this.mfa !== MFA_DISABLED;
  }

  shouldResetMFA() {
    return this.mfa === MFA_RESET;
  }

  isMfaEnforced() {
    return Boolean(this.mfaEnforced);
  }

  hasOAuthProvider() {
    return this.oauthProviders.size > 0;
  }

  hasGoogleOAuth() {
    return this.oauthProviders.some((provider) => provider === GOOGLE_AUTH_KIND);
  }

  hasGithubOAuth() {
    return this.oauthProviders.some((provider) => provider === GITHUB_AUTH_KIND);
  }

  hasVercelOAuth() {
    return this.oauthProviders.some((provider) => provider === VERCEL_MARKETPLACE_AUTH_KIND);
  }

  getEmailDomain() {
    return this.email.split('@')[1];
  }
}

export function createMember(props: CreateFunctionInput<Member> = {}) {
  if (props instanceof Member) {
    return props;
  }

  return new Member(fromJS(props))
    .update('customRoles', (rs) => rs.toOrderedSet())
    .update('excludedDashboards', (ed) => ed?.toSet())
    .update('teams', (team) => team?.map((t) => new MemberTeams(t)))
    .update('permissionGrants', (permissionGrant) => permissionGrant?.map((g) => new MemberPermissionGrants(g)));
}
